import 'svelte/internal/disclose-version';
import * as $ from 'svelte/internal/client';
import TopBar from '../components/layout/TopBar.svelte';
import Timer from '../components/session/Timer.svelte';
import VolumePicker from '../components/session/VolumePicker.svelte';
import Button from '../components/shared/Button.svelte';
import AddToStorage from '../components/storage/AddToStorage.svelte';
import { getActiveTimer, startTimer, stopTimer } from '../lib/stores/session.svelte.ts';
import { saveSession } from '../lib/services/session';
import { getSettings } from '../lib/stores/settings.svelte.ts';
import { getActiveChild } from '../lib/stores/children.svelte.ts';
import { navigate } from '../lib/router.svelte.ts';
import { mlToOz, ozToMl } from '../lib/utils/units.ts';
import { showToast } from '../components/shared/Toast.svelte';

var root_2 = $.from_html(`<button class="note-toggle svelte-3mwubrdp4ofh1">+ Add note</button>`);
var root_3 = $.from_html(`<textarea class="note-input svelte-3mwubrdp4ofh1" placeholder="Optional note..." rows="2"></textarea>`);
var root_1 = $.from_html(`<div class="timer-section svelte-3mwubrdp4ofh1"><p class="label svelte-3mwubrdp4ofh1">Pumping</p> <!></div> <div class="actions svelte-3mwubrdp4ofh1"><!> <!></div>`, 1);
var root_5 = $.from_html(`<div class="volume-section svelte-3mwubrdp4ofh1"><h3 class="svelte-3mwubrdp4ofh1">How much did you pump?</h3> <!></div> <div class="actions svelte-3mwubrdp4ofh1"><!></div>`, 1);
var root_7 = $.from_html(`<div class="conflict svelte-3mwubrdp4ofh1"><p class="conflict-message svelte-3mwubrdp4ofh1"> </p> <!></div>`);
var root_9 = $.from_html(`<p class="loading svelte-3mwubrdp4ofh1">Starting timer…</p>`);
var root = $.from_html(`<!> <div class="session-page svelte-3mwubrdp4ofh1"><!></div> <!>`, 1);

function PumpingSession($$anchor, $$props) {
	$.push($$props, true);

	const timer = $.derived(getActiveTimer);
	const settings = $.derived(getSettings);
	const activeChild = $.derived(getActiveChild);
	const isImperial = $.derived(() => $.get(settings).units === 'imperial');
	let phase = $.state('timer');

	// Internal volume always stored in mL — pre-fill from active child's last pump volume
	let volumeMl = $.state(60);

	const displayVolume = $.derived(() => $.get(isImperial) ? mlToOz($.get(volumeMl)) : $.get(volumeMl));

	$.user_effect(() => {
		// Re-pre-fill when active child changes and we're not yet in volume phase
		if ($.get(phase) === 'timer' && $.get(activeChild)?.lastPumpVolume) {
			$.set(volumeMl, $.get(activeChild).lastPumpVolume, true);
		}
	});

	function handleVolumeChange(v) {
		$.set(volumeMl, $.get(isImperial) ? ozToMl(v) : v, true);
	}

	let note = $.state('');
	let showNote = $.state(false);
	let stoppedTimer = null;
	let savedSessionId = $.state(null);
	let showAddToStorage = $.state(false);

	$.user_effect(() => {
		// Only auto-start when there is no active timer at all (QA-003)
		if (!$.get(timer) && $.get(phase) === 'timer' && $.get(activeChild)) {
			startTimer($.get(activeChild).id, 'pumping');
		}
	});

	async function handleEnd() {
		// Set phase first (synchronously) so the auto-start $effect sees phase !== 'timer'
		// before activeTimer becomes null inside stopTimer — prevents race-condition restart
		$.set(phase, 'volume');

		const stopped = await stopTimer();

		if (!stopped) {
			$.set(phase, 'timer');

			return;
		}

		// Capture endTime now so duration is not inflated by time spent on volume screen (QA-013)
		stoppedTimer = {
			startTime: stopped.startTime,
			endTime: Date.now(),
			childId: stopped.childId
		};
	}

	async function handleSave() {
		if (!stoppedTimer) return;

		try {
			const session = await saveSession(stoppedTimer.childId, {
				type: 'pumping',
				startTime: stoppedTimer.startTime,
				endTime: stoppedTimer.endTime,
				durationMs: stoppedTimer.endTime - stoppedTimer.startTime,
				volumeMl: $.get(volumeMl),
				note: $.get(note) || undefined
			});

			$.set(savedSessionId, session.id, true);
			$.set(showAddToStorage, true);
		} catch(e) {
			showToast('Failed to save session. Please try again.', 'error');
		}
	}

	var fragment = root();
	var node = $.first_child(fragment);

	TopBar(node, { title: 'Pumping', showBack: true, backPath: '/log' });

	var div = $.sibling(node, 2);
	var node_1 = $.child(div);

	{
		var consequent_1 = ($$anchor) => {
			var fragment_1 = root_1();
			var div_1 = $.first_child(fragment_1);
			var node_2 = $.sibling($.child(div_1), 2);

			Timer(node_2, {
				get startTime() {
					return $.get(timer).startTime;
				}
			});

			$.reset(div_1);

			var div_2 = $.sibling(div_1, 2);
			var node_3 = $.child(div_2);

			{
				var consequent = ($$anchor) => {
					var button = root_2();

					$.delegated('click', button, () => $.set(showNote, true));
					$.append($$anchor, button);
				};

				var alternate = ($$anchor) => {
					var textarea = root_3();

					$.remove_textarea_child(textarea);
					$.bind_value(textarea, () => $.get(note), ($$value) => $.set(note, $$value));
					$.append($$anchor, textarea);
				};

				$.if(node_3, ($$render) => {
					if (!$.get(showNote)) $$render(consequent); else $$render(alternate, -1);
				});
			}

			var node_4 = $.sibling(node_3, 2);

			Button(node_4, {
				variant: 'primary',
				size: 'lg',
				fullWidth: true,
				onclick: handleEnd,
				children: ($$anchor, $$slotProps) => {
					$.next();

					var text = $.text('End Session');

					$.append($$anchor, text);
				},
				$$slots: { default: true }
			});

			$.reset(div_2);
			$.append($$anchor, fragment_1);
		};

		var consequent_2 = ($$anchor) => {
			var fragment_2 = root_5();
			var div_3 = $.first_child(fragment_2);
			var node_5 = $.sibling($.child(div_3), 2);

			{
				let $0 = $.derived(() => $.get(isImperial) ? 'oz' : 'mL');
				let $1 = $.derived(() => $.get(isImperial) ? 0.5 : 5);
				let $2 = $.derived(() => $.get(isImperial) ? 17 : 500);

				VolumePicker(node_5, {
					get value() {
						return $.get(displayVolume);
					},
					onchange: handleVolumeChange,
					get unit() {
						return $.get($0);
					},

					get step() {
						return $.get($1);
					},

					get max() {
						return $.get($2);
					}
				});
			}

			$.reset(div_3);

			var div_4 = $.sibling(div_3, 2);
			var node_6 = $.child(div_4);

			Button(node_6, {
				variant: 'primary',
				size: 'lg',
				fullWidth: true,
				onclick: handleSave,
				children: ($$anchor, $$slotProps) => {
					$.next();

					var text_1 = $.text('Save Session');

					$.append($$anchor, text_1);
				},
				$$slots: { default: true }
			});

			$.reset(div_4);
			$.append($$anchor, fragment_2);
		};

		var consequent_3 = ($$anchor) => {
			var div_5 = root_7();
			var p = $.child(div_5);
			var text_2 = $.child(p);

			$.reset(p);

			var node_7 = $.sibling(p, 2);

			Button(node_7, {
				variant: 'primary',
				fullWidth: true,
				onclick: () => navigate(`/session/${$.get(timer).sessionType}`),
				children: ($$anchor, $$slotProps) => {
					$.next();

					var text_3 = $.text('Go to active session');

					$.append($$anchor, text_3);
				},
				$$slots: { default: true }
			});

			$.reset(div_5);
			$.template_effect(() => $.set_text(text_2, `A ${$.get(timer).sessionType ?? ''} session is already active.`));
			$.append($$anchor, div_5);
		};

		var alternate_1 = ($$anchor) => {
			var p_1 = root_9();

			$.append($$anchor, p_1);
		};

		$.if(node_1, ($$render) => {
			if ($.get(phase) === 'timer' && $.get(timer) && $.get(timer).sessionType === 'pumping') $$render(consequent_1); else if ($.get(phase) === 'volume') $$render(consequent_2, 1); else if ($.get(timer) && $.get(timer).sessionType !== 'pumping') $$render(consequent_3, 2); else $$render(alternate_1, -1);
		});
	}

	$.reset(div);

	var node_8 = $.sibling(div, 2);

	{
		let $0 = $.derived(() => $.get(savedSessionId) || undefined);

		AddToStorage(node_8, {
			get open() {
				return $.get(showAddToStorage);
			},

			get sessionId() {
				return $.get($0);
			},

			get initialVolume() {
				return $.get(volumeMl);
			},
			onclose: () => navigate('/'),
			onsaved: () => navigate('/')
		});
	}

	$.append($$anchor, fragment);
	$.pop();
}

if (import.meta.hot) {
	PumpingSession = $.hmr(PumpingSession);

	import.meta.hot.accept((module) => {
		$.cleanup_styles('svelte-3mwubrdp4ofh1');
		PumpingSession[$.HMR].update(module.default);
	});
}

export default PumpingSession;

$.delegate(['click']);
import "bun-svelte:PumpingSession.svelte-2mm8esjm0udmk-style.css";